/**
 * Friends Service
 * Frontend service for friend management and real-time location sharing
 * Similar to crews but for 1-to-1 friend connections with mutual location sharing
 */

import { telegram } from './telegram';

export interface Friend {
  id: string;
  telegram_user_id: string;
  telegram_username: string | null;
  display_name: string | null;
  status: 'pending' | 'accepted' | 'blocked';
  location_sharing: boolean; // Whether THIS user shares location with friend
  friend_shares_location: boolean; // Whether FRIEND shares location with us
  last_latitude: number | null;
  last_longitude: number | null;
  last_location_update: string | null;
  created_at: string;
  updated_at: string;
}

export interface FriendRequest {
  id: string;
  from_user_id: string;
  from_username: string | null;
  to_user_id: string;
  to_username: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
}

export interface FriendLocation {
  friend_id: string;
  telegram_user_id: string;
  username: string | null;
  display_name: string | null;
  latitude: number;
  longitude: number;
  updated_at: string;
  // Optional: speed/heading for smooth map animation
  speed?: number;
  heading?: number;
}

const API_BASE = '/api/friends';

function getHeaders(): HeadersInit {
  // Use telegram service which has proper fallback logic for pages.dev
  let userId = telegram.getUserId() || '';
  let username = window.Telegram?.WebApp?.initDataUnsafe?.user?.username || '';

  // Fallback for web preview/local testing
  if (!userId) {
    const storedId = localStorage.getItem('dev-user-id');
    const storedName = localStorage.getItem('dev-username');
    if (storedId) {
      userId = storedId;
    } else {
      userId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `dev-${crypto.randomUUID()}`
        : `dev-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('dev-user-id', userId);
    }

    if (storedName) {
      username = storedName;
    } else {
      username = 'web-user';
      localStorage.setItem('dev-username', username);
    }
  }

  return {
    'Content-Type': 'application/json',
    'X-Telegram-User-Id': userId,
    'X-Telegram-Username': username,
    'X-Telegram-Init-Data': telegram.getInitData(),
  };
}

class FriendsService {
  /**
   * Get all friends for the current user
   */
  async getMyFriends(): Promise<{ friends: Friend[]; pendingRequests: FriendRequest[] }> {
    const response = await fetch(`${API_BASE}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load friends' }));
      throw new Error(error.error || 'Failed to load friends');
    }

    return response.json();
  }

  /**
   * Send a friend request to another user by Telegram username
   */
  async sendFriendRequest(username: string): Promise<FriendRequest> {
    const response = await fetch(`${API_BASE}/request`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to send request' }));
      throw new Error(error.error || 'Failed to send friend request');
    }

    return response.json();
  }

  /**
   * Accept a pending friend request
   */
  async acceptFriendRequest(requestId: string): Promise<Friend> {
    const response = await fetch(`${API_BASE}/request/${requestId}/accept`, {
      method: 'POST',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to accept request' }));
      throw new Error(error.error || 'Failed to accept friend request');
    }

    return response.json();
  }

  /**
   * Reject a pending friend request
   */
  async rejectFriendRequest(requestId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/request/${requestId}/reject`, {
      method: 'POST',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to reject request' }));
      throw new Error(error.error || 'Failed to reject friend request');
    }
  }

  /**
   * Remove a friend
   */
  async removeFriend(friendId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${friendId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to remove friend' }));
      throw new Error(error.error || 'Failed to remove friend');
    }
  }

  /**
   * Toggle location sharing with a specific friend
   */
  async toggleLocationSharing(friendId: string, enabled: boolean): Promise<Friend> {
    const response = await fetch(`${API_BASE}/${friendId}/location-sharing`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update sharing' }));
      throw new Error(error.error || 'Failed to update location sharing');
    }

    return response.json();
  }

  /**
   * Update own location (called periodically when sharing is enabled)
   */
  async updateMyLocation(latitude: number, longitude: number, speed?: number, heading?: number): Promise<void> {
    const response = await fetch(`${API_BASE}/location`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ latitude, longitude, speed, heading }),
    });

    if (!response.ok) {
      console.warn('Failed to update location');
    }
  }

  /**
   * Get real-time locations of friends who are sharing with us
   */
  async getFriendsLocations(): Promise<FriendLocation[]> {
    const response = await fetch(`${API_BASE}/locations`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.locations || [];
  }

  /**
   * Block a user (prevents friend requests and removes existing friendship)
   */
  async blockUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/block`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to block user' }));
      throw new Error(error.error || 'Failed to block user');
    }
  }

  /**
   * Generate a friend invite link that can be shared via Telegram
   */
  async getInviteLink(): Promise<string> {
    const response = await fetch(`${API_BASE}/invite-link`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get invite link' }));
      throw new Error(error.error || 'Failed to get invite link');
    }

    const data = await response.json();
    return data.link;
  }
}

export const friendsService = new FriendsService();
export default friendsService;
